import {
	BaseTransactionAddress,
	Lawyer,
	MortgageInstructionGuarantor,
	MortgageInstructionMortgagor,
	TransactionBase,
} from './mortgages/mortgage-base';

export enum OriginatorTypeEnum {
	LENDER = 'LENDER',
	LAWYER = 'LAWYER',
	UNKNOWN = 'UNKNOWN',
}

export interface LLCDocument {
	documentId: string;
	documentType: string;
	documentDate: string;
	originator: OriginatorTypeEnum;
	isGenerated: boolean;
	documentNames: {
		language: string;
		name: string;
	}[];
	downloaded: boolean;
	downloadedAt?: number | null;
	gcsPath?: string;
}

export interface LLCChangeSummary {
	lastUpdated: number;
	newTransactions: number;
	assigned: number;
	unassigned: number;
	cancellationRequested: number;
	newLenderChanges: number;
	newLenderNotes: number;
}

export interface LLCTransaction extends TransactionBase {
	data: LLCTransactionData | null;
	documentList?: { [id: string]: LLCDocument };
}

export interface LLC {
	lastUpdated: number | null;
}

export interface BridgeLoan {
	address: BaseTransactionAddress;
	firstPaymentDate: string;
	interestAdjustmentDate: string;
	interestRate: number;
	loanAmount: number;
	maturityDate: string;
}

export interface LLCContact {
	firstName: string;
	lastName: string;
	middleName: string;
	phone: string;
	email: string;
}

export interface Branch extends LLCContact {
	address: BaseTransactionAddress;
	fax: string;
	transitNumber: string;
	name: string;
}

export interface LLCText {
	textEn: string;
	textFr: string;
}

export interface LLCInterestRate {
	actualMortgageRate?: number;
	actualMortgageRateText: LLCText;
	baseRate?: number;
	baseRateText: LLCText;
	earlyPaymentAmount: number;
	equivalentRate: number;
	fixedRate?: number;
	incrementAboveBelowPrime: number;
	interestRateType: 'FIXED | VAIRABLE';
	maximumChargeRate: number;
}

export interface LLCRegistrationInstructions {
	increment: number;
	interestRate?: number;
	interestRateType: string;
	mortgageType: string;
}

export interface LLCMortgage {
	amortizationPeriod: number;
	assignmentOfRents: boolean;
	calculationPeriod: {
		calculationPeriodText: LLCText;
		code: string;
	};
	cashBackAmount: number;
	closingDate: string;
	constructionMortgage: boolean;
	interestAdjustmentDate?: string;
	interestAdjustmentDateText: LLCText;
	interestRate: {
		actualMortgageRate?: number;
		actualMortgageRateText: LLCText;
		baseRate?: number;
		baseRateText: LLCText;
		earlyPaymentAmount: number;
		equivalentRate: number;
		fixedRate?: number;
		incrementAboveBelowPrime: number;
		interestRateType: 'FIXED | VAIRABLE';
		maximumChargeRate: number;
	};
	loanApplicationDate: string;
	maturityDate?: string;
	maturityDateText: LLCText;
	mortgageAmountAdvanced: number;
	mortgagee: string;
	mortgageIlas: { firstName: string; lastName: string; middleName: string }[];
	mortgageInsurer: string;
	mortgageNumber: string;
	mortgagePayments: MortgagePayment[];
	mortgageProduct: string;
	mortgageTerm: number;
	purchasePrice?: number;
	rateExpiryDate: Date;
	ratioIndicator: string;
	registeredAmount?: number;
	registeredAmountText?: LLCText;
	registeredInterestRateText: LLCText;
	registrationInstructions: LLCRegistrationInstructions;
	registrationPaymentFreq: string;
	solicitorClose: boolean;
	standardChargeTermText: LLCText;
	totalLoanAmount: number;
}

export interface MortgagePayment {
	acceleratedPayment: boolean;
	firstPaymentDate?: string;
	firstPaymentDateText: LLCText;
	insuranceAmount: number;
	MortgagePaymentType: string;
	paymentAmount?: number;
	paymentAmountText: LLCText;
	paymentDateAndPeriodText: LLCText;
	paymentFrequency?: string;
	propertyTaxAmount: number;
	total?: number;
}

export interface MortgageLoanFees {
	appraisalFee: number;
	bridgeLoanApplicationFee: number;
	buyDownAtCommitmentFee: number;
	fctFee: number;
	holdBackFee: number;
	insuranceApplicationFee: number;
	insurancePremium: number;
	internalMortgagePayout: number;
	lenderFee: number;
	lineOfCredit: number;
	miscellaneousFee: number;
	netMortgageAdvance: number;
	rebate: number;
	taxHoldback: number;
	taxOnInsurancePremium: number;
	wireFee: number;
}

export interface ExistingMortgage {
	action: string;
	amount: number;
	mortgagee: string;
	fireInsurancePolicy: string;
	id: number;
	instrumentNumber: string;
	isCondominium?: boolean;
	legalDescription: string;
	lenderCollectsPropertyTaxes: boolean;
	lroNumber: string;
	mortgagePriority: string;
	municipality: string;
	newConstruction?: boolean;
	newHomeWarranty?: boolean;
	numberOfUnits: string;
	occupancyType: string;
	otherEstateTypeDescription: string;
	propertyIdentificationNumbers: string[];
	registeredOwners: string[];
	registrationDate?: Date;
	registryOffice: string;
}

export type LLCPriority = 'FIRST' | 'SECOND' | 'THIRD' | 'FOURTH' | 'FIFTH';

export interface LLCProperty {
	id: number;
	address: BaseTransactionAddress;
	annualTaxAmount: string;
	assessmentRollNumbers: string;
	condoBookNumberOfDeclaration: string;
	condoCorporationNumber: string;
	condoDeclarationAcceptedDate?: Date;
	condoDeclarationDate?: Date;
	condoDeclarationRegisterationNumber: string;
	condoDeclarationRegistrationDate?: Date;
	condoLevel: string;
	condoPageNumberOfDeclaration: string;
	condoPlanNumber: string;
	condoPlanRegistrationDate?: Date;
	condoUnitNumber: string;
	estateType: string;
	otherEstateTypeDescription: string;
	existingMortgages: ExistingMortgage[];
	lenderCollectsPropertyTaxes: boolean;
	legalDescription: string;
	mortgagePriority: LLCPriority;
}

export interface LLCTransactionData {
	bridgeLoan: BridgeLoan;
	dealSource: {
		branch: Branch;
		sourceType: string;
	};
	encumbrances: string;
	funding: {
		accountNumber: string;
		bankName: string;
		bankNumber: string;
		fundsOnHold: boolean;
		paymentMethod: string;
		rffRequired: boolean;
		transitNumber: string;
	};
	guarantors: MortgageInstructionGuarantor[];
	lawyer: Lawyer;
	lenderCode: string;
	mmsLenderCode: string;
	lenderComments: string;
	lenderReferenceNumber: string;
	mailingAddressType: 'DEAL SOURCE' | 'MORTGAGE CENTER' | 'OWNING BRANCH';
	mortgage: LLCMortgage;
	mortgageCentre: Branch;
	mortgageLoanFees: MortgageLoanFees;
	mortgageOwningBranch: Branch;
	mortgagors: MortgageInstructionMortgagor[];
	properties: LLCProperty[];
	serviceAddress: string;
	serviceAddressType: 'DEAL SOURCE' | 'MORTGAGE CENTER' | 'OWNING BRANCH';
	solicitorConditions: { clause: string; clauseSequence: string }[];
	sourceFctUrn: string;
	transactionId: string;
	transactionType: string;
	workflowModelId: string;
	propertyAddress: any;
	mortgageType: any;
	mortgagePayment: any;
	purchasePrice: any;
}

export enum AdvancedAmountLabels {
	FCT_FEE = 'FCT Fee',
	REBATE = 'Rebate',
	INTERNAL_MORTGAGE_PAYOUT = 'Internal mortgage payout',
	TAX_HOLDBACK = 'Tax holdback',
	LINE_OF_CREDIT = 'Line of credit',
	BUY_DOWN_COMMITMENT_FEE = 'Buy down at commitment fee',
	TAX_INSURANCE_PREMIUM = 'Tax insurance premium',
	WIRE_FEE = 'Wire fee',
	MISC_FEE = 'Miscellaneous fee',
	LENDER_FEE = 'Lender fee',
	INSURANCE_PREMIUM = 'Insurance premium',
	INSURANCE_APP_FEE = 'Insurance application fee',
	HOLD_BACK_FEE = 'Holdback fee',
	APPRAISAL_FEE = 'Appraisal fee',
	BRIDGE_LOAN_APP_FEE = 'Bridge loan application fee',
}
